import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BaseService } from '@core';
import { ActiveStatusEnum } from '@models';

import {
  PugTransferApiService,
  PugTransferBoardDto,
  PugTransferTileDto,
  PugTransferUserDto,
} from '../services/pug-transfer-api.service';
import { ProgramFrmComponent } from './program-frm.component';

/**
 * Program User Group Transfer — moves many Account Users between groups at once.
 *
 * The case it exists for: people change location, each location is a group carrying its own
 * purchase order, and future orders have to bill to the new one. Doing that a user at a time
 * through the Account User page is unworkable at sixty people.
 *
 * IT LIVES INSIDE THE PROGRAM because the program is what makes a transfer safe and decidable.
 * A group reaches a program only through that program's approval tree, so one group can serve
 * several; without a fixed program, dropping a user from one group onto another is undecidable
 * whenever the two share more than one. Sitting in the program context also means the account and
 * the program are already in scope, so the board asks for neither.
 *
 * SAVES ON DROP. No pending change set and no Apply button: one selected user saves immediately,
 * more than one asks first. The server does each drop in a single transaction.
 *
 * Users with an order in flight are LOCKED and cannot be dragged. That is not a nicety — a
 * transfer excludes the old group, which strands in-flight orders exactly the way deleting the
 * membership would, and unlike the delete path nothing on the server refuses it.
 */
@Component({
  selector: 'prox-program-pug-transfer-sub',
  templateUrl: './program-pug-transfer.sub.component.html',
  standalone: false,
  styles: [
    `
      /* A plain list rather than a grid: rows are drag sources, and ag-Grid owns its row DOM in a
         way that fights a drag handle. Layout only — color comes from theme tokens. */
      .roster {
        overflow-y: auto;
      }
      .roster-row {
        cursor: grab;
        border-bottom: 1px solid var(--surface-border);
      }
      .roster-row.locked {
        cursor: not-allowed;
        opacity: 0.55;
      }
      .roster-row.selected {
        background: var(--p-primary-50);
      }
      /* The QuickBooks theme has no drag vocabulary at all — no tile, drop or drag classes exist
         in any of the three protected files — so these are genuinely new rather than duplicates
         of something already themed.

         Deliberately loud: a thin dashed outline was too easy to miss mid-drag, when the pointer
         is moving and carrying a drag image. Solid ring, tint and lift together read at a glance
         without having to look for them. */
      .tile-drop-target {
        outline: 3px solid var(--p-primary-color);
        outline-offset: -3px;
        background: var(--p-primary-50);
        box-shadow: 0 4px 12px rgb(0 0 0 / 15%);
        transform: translateY(-1px);
      }
      .tile-disabled {
        opacity: 0.55;
      }
    `,
  ],
})
export class ProgramPugTransferSubComponent implements OnChanges {
  @Input() parent!: ProgramFrmComponent;
  @Input() visible!: boolean;

  board?: PugTransferBoardDto;
  selectedUserIds = new Set<string>();
  dragOverTileId?: string;

  /** Filters the roster by the group a user currently sits in. Filtering never touches
   *  selectedUserIds, so ticks made under one filter survive changing or clearing it. */
  pugFilterId?: string;

  /** Everyone moved since this panel was opened, so their rows can be badged. Kept across the
   *  board reload that follows each drop, which is the only reason it lives here and not in the
   *  transfer result. */
  movedThisSession = new Set<string>();

  loading = false;
  transferring = false;
  loadError = '';
  lastResultMessage = '';

  private loadedProgramId?: string;

  constructor(
    private api: PugTransferApiService,
    private baseService: BaseService,
  ) {}

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['visible'] && this.visible) {
      await this.refresh(false);
    }
  }

  /** ISubComponent. Loads on first reveal and on an explicit refresh; the board is a read of
   *  live membership, so it must not be cached across a program change. */
  async refresh(forceRefresh: boolean) {
    if (!this.visible) return;
    const programId = this.parent?.program?.id;
    if (!programId) return;
    if (!forceRefresh && this.loadedProgramId === programId && this.board) return;
    await this.loadBoard();
  }

  private async loadBoard() {
    const program = this.parent.program;
    this.loading = true;
    this.loadError = '';
    try {
      this.board = await this.api.getBoard(program.accountId, program.id).toPromise();
      this.loadedProgramId = program.id;
    } catch (err: any) {
      this.loadError = this.errorText(err, 'Could not load the transfer board.');
      this.board = undefined;
    } finally {
      this.loading = false;
    }
  }

  // ---- Roster selection -------------------------------------------------------------------

  isLocked(u: PugTransferUserDto): boolean {
    return u.LockedReason != null;
  }

  isSelected(u: PugTransferUserDto): boolean {
    return this.selectedUserIds.has(u.Id);
  }

  toggleUser(u: PugTransferUserDto) {
    if (this.isLocked(u) || this.transferring) return;
    if (this.selectedUserIds.has(u.Id)) {
      this.selectedUserIds.delete(u.Id);
    } else {
      this.selectedUserIds.add(u.Id);
    }
  }

  /**
   * The rows on screen. Filtering is a VIEW concern only — it never removes anyone from
   * selectedUserIds, so a tick made under one filter is still there after the filter changes or
   * clears. Dropping a filtered-out but still-ticked user would silently move somebody the
   * administrator can no longer see.
   */
  get visibleUsers(): PugTransferUserDto[] {
    const users = this.board?.Users ?? [];
    if (!this.pugFilterId) return users;
    if (this.pugFilterId === this.NO_GROUP) return users.filter((u) => u.CurrentPugId == null);
    return users.filter((u) => u.CurrentPugId === this.pugFilterId);
  }

  /** Groups to filter by, plus an entry for users no group currently governs. */
  get pugFilterOptions(): { label: string; value: string }[] {
    const opts = (this.board?.Tiles ?? []).map((t) => ({ label: t.Name, value: t.Id }));
    if ((this.board?.Users ?? []).some((u) => u.CurrentPugId == null)) {
      opts.push({ label: '(no group governs this program)', value: this.NO_GROUP });
    }
    return opts;
  }

  readonly NO_GROUP = '--none--';

  /** Select all acts on what is on screen, so it means what it says under a filter. */
  get selectableUsers(): PugTransferUserDto[] {
    return this.visibleUsers.filter((u) => !this.isLocked(u));
  }

  /** Ticked but filtered out of view — worth saying, since they will still move on a drop. */
  get hiddenSelectedCount(): number {
    const visible = new Set(this.visibleUsers.map((u) => u.Id));
    return Array.from(this.selectedUserIds).filter((id) => !visible.has(id)).length;
  }

  hasMoved(u: PugTransferUserDto): boolean {
    return this.movedThisSession.has(u.Id);
  }

  /**
   * Who is currently ticked, by name, INCLUDING anyone the filter is hiding.
   *
   * That is the whole point: a selection built across several filters is invisible on the roster,
   * so the status box is the only place the administrator can see the full set they are about to
   * move. Reads off board.Users rather than the visible rows for exactly that reason.
   */
  get selectedNames(): string[] {
    const byId = new Map((this.board?.Users ?? []).map((u) => [u.Id, u.Name]));
    return Array.from(this.selectedUserIds)
      .map((id) => byId.get(id))
      .filter((n): n is string => n != null)
      .sort((a, b) => a.localeCompare(b));
  }

  /** The pending half of the status box: who would move if a tile were dropped on now. */
  get movingMessage(): string {
    const names = this.selectedNames;
    if (names.length === 0) return '';
    return `Moving ${names.length} ${names.length === 1 ? 'Account User' : 'Account Users'}: `
         + this.summarizeNames(names);
  }

  /** Names joined, truncated the way the user-management action panel truncates its list. */
  private summarizeNames(names: string[]): string {
    const shown = names.slice(0, 10).join('; ');
    const rest = names.length - 10;
    return rest > 0 ? `${shown} and ${rest} other${rest === 1 ? '' : 's'}` : shown;
  }

  get selectedCount(): number {
    return this.selectedUserIds.size;
  }

  get allSelectableSelected(): boolean {
    const selectable = this.selectableUsers;
    return selectable.length > 0 && selectable.every((u) => this.selectedUserIds.has(u.Id));
  }

  toggleSelectAll() {
    if (this.allSelectableSelected) {
      this.selectedUserIds.clear();
    } else {
      this.selectableUsers.forEach((u) => this.selectedUserIds.add(u.Id));
    }
  }

  get lockedCount(): number {
    return (this.board?.Users ?? []).filter((u) => this.isLocked(u)).length;
  }

  // ---- Drag and drop ----------------------------------------------------------------------

  /**
   * Dragging an unselected row implicitly selects it and drops only that user. Dragging one of
   * several ticked rows drops the whole selection. Anything else would silently move a different
   * set of people from the one on screen.
   */
  onDragStart(u: PugTransferUserDto) {
    if (this.isLocked(u)) return;
    if (!this.selectedUserIds.has(u.Id)) {
      this.selectedUserIds.clear();
      this.selectedUserIds.add(u.Id);
    }
  }

  onDragEnter(tile: PugTransferTileDto) {
    if (this.canReceive(tile)) this.dragOverTileId = tile.Id;
  }

  /**
   * Only clear when leaving the tile that is actually highlighted.
   *
   * A native dragleave fires every time the pointer crosses into a CHILD element, and the tile is
   * full of children — the name, each detail row, the warning box. Clearing unconditionally made
   * the highlight strobe on and off as the pointer moved across it, which is why it read as not
   * working at all. Entering the next tile sets its own id, so the outgoing one is replaced
   * rather than needing to be cleared.
   */
  onDragLeave(tile: PugTransferTileDto) {
    if (this.dragOverTileId === tile.Id) this.dragOverTileId = undefined;
  }

  /** A drag abandoned outside any tile leaves no highlight behind. */
  onRosterDragEnd() {
    this.dragOverTileId = undefined;
  }

  canReceive(tile: PugTransferTileDto): boolean {
    return tile.CannotReceiveReason == null;
  }

  async onDrop(tile: PugTransferTileDto) {
    this.dragOverTileId = undefined;
    if (!this.canReceive(tile) || this.transferring) return;

    const ids = Array.from(this.selectedUserIds);
    if (ids.length === 0) return;

    // One user goes straight through. More than one asks first — a bulk move is where a mis-drop
    // is both easiest to make and hardest to notice. A tile warning rides along in the question,
    // since that is the last moment it can change the administrator's mind.
    if (ids.length > 1) {
      const caveat = tile.WarningReason ? `<br/><br/>${tile.WarningReason}` : '';
      const ok = await this.baseService.dialogService.verifyProceedDialog(
        'Transfer Account Users?',
        `Transfer <b>${ids.length}</b> Account Users to Program User Group ` +
          `<b>"${tile.Name}"</b> for ${this.parent.program.name}?` +
          caveat,
      );
      if (!ok) return;
    }

    await this.doTransfer(tile, ids);
  }

  private async doTransfer(tile: PugTransferTileDto, accountUserIds: string[]) {
    const program = this.parent.program;
    this.transferring = true;
    this.loadError = '';
    this.lastResultMessage = '';
    try {
      const r = await this.api
        .transfer({
          AccountId: program.accountId,
          ProgramId: program.id,
          TargetProgramUserGroupId: tile.Id,
          AccountUserIds: accountUserIds,
        })
        .toPromise();

      r!.TransferredUserIds.forEach((id) => this.movedThisSession.add(id));

      // Name them. A bare count cannot be checked against intent, and this is the only moment
      // the administrator can see that who moved is who they meant to move.
      const names = r!.TransferredUserNames;
      const who = names.length > 0 ? this.summarizeNames(names) : 'nobody';

      const parts: string[] = [`Moved to ${r!.TargetPugName}: ${who}`];
      if (r!.AllowanceAdjustments > 0) {
        parts.push(`${this.fmt(r!.AllowanceAmtCarried)} of spend carried forward`);
      }
      if (r!.IssuanceAdjustments > 0) {
        parts.push(`${r!.IssuanceAdjustments} issuance ${r!.IssuanceAdjustments === 1 ? 'limit' : 'limits'} carried`);
      }
      if (r!.OrdersRepointed > 0) {
        parts.push(`${r!.OrdersRepointed} open ${r!.OrdersRepointed === 1 ? 'order' : 'orders'} re-pointed`);
      }
      if (r!.OrdersReturnedToSetup > 0) {
        const n = r!.OrdersReturnedToSetup;
        parts.push(`${n} collected ${n === 1 ? 'order' : 'orders'} returned to Setup (target batch already released)`);
      }
      if (r!.UsersSkipped > 0) {
        parts.push(`${r!.UsersSkipped} skipped`);
      }
      this.lastResultMessage = parts.join(' · ');
      if (r!.Problems.length > 0) {
        this.loadError = r!.Problems.join('  ');
      }

      this.selectedUserIds.clear();
      // Re-read rather than patching locally: a transfer changes member counts on two tiles and
      // can change which group governs a user, and a hand-maintained copy of that drifts.
      await this.loadBoard();
    } catch (err: any) {
      this.loadError = this.errorText(err, 'The transfer failed. Nothing was applied.');
    } finally {
      this.transferring = false;
    }
  }

  // ---- Display ----------------------------------------------------------------------------

  /** Groups on one program are not interchangeable, so a tile must say what differs. */
  orderWindowText(tile: PugTransferTileDto): string {
    if (tile.OrderTimeFrameStartDays == null && tile.OrderTimeFrameDurationDays == null) return '';
    const start = tile.OrderTimeFrameStartDays ?? 0;
    const days = tile.OrderTimeFrameDurationDays;
    return days == null ? `opens day ${start}` : `day ${start}, ${days} days`;
  }

  isInactive(tile: PugTransferTileDto): boolean {
    return tile.ActiveStatusId === ActiveStatusEnum.Inactive;
  }

  fmt(n: number | null | undefined): string {
    return (n ?? 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  }

  private errorText(err: any, fallback: string): string {
    return err?.error?.Message ?? err?.error ?? err?.message ?? fallback;
  }
}
